import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Award,
  Target,
  TrendingUp,
  User,
  AlertTriangle,
  Users,
  Activity,
  Shield,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { usersApi, analyticsApi, plagiarismApi } from '@/api/endpoints';
import { User as UserType, StudentAnalytics } from '@/types';
import { format } from 'date-fns';
import { Card, StatCard, Badge, SearchInput } from '@azyntor-labs/nexus-ui';

export const StudentProgressPage = () => {
  const [searchParams] = useSearchParams();
  const studentParam = searchParams.get('student') || '';
  const [selectedStudentId, setSelectedStudentId] = useState(studentParam);
  const [studentSearch, setStudentSearch] = useState('');

  // Fetch students
  const { data: studentsRes, isLoading: studentsLoading } = useQuery({
    queryKey: ['students-for-progress'],
    queryFn: () => usersApi.list({ role: 'student', per_page: 200 }),
    staleTime: 30000,
  });

  const students: UserType[] = useMemo(() => {
    const data = studentsRes?.data?.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as any).data)) {
      return (data as any).data;
    }
    return [];
  }, [studentsRes]);

  // Auto-select student from URL param
  useEffect(() => {
    if (studentParam && students.length > 0 && !studentSearch) {
      const found = students.find((s) => s.id === studentParam);
      if (found) {
        setSelectedStudentId(studentParam);
        setStudentSearch(
          found.first_name || found.last_name
            ? `${found.first_name || ''} ${found.last_name || ''}`.trim()
            : found.email
        );
      }
    }
  }, [studentParam, students.length]);

  // Helper to get display name (fallback to email if no first/last name)
  const getDisplayName = (s: UserType) => {
    if (s.first_name || s.last_name) {
      return `${s.first_name || ''} ${s.last_name || ''}`.trim();
    }
    return s.email;
  };

  // Build suggestions for SearchInput
  const studentSuggestions = useMemo(() => {
    return students
      .filter((s) => {
        if (!studentSearch) return true;
        const q = studentSearch.toLowerCase();
        return (
          s.first_name?.toLowerCase().includes(q) ||
          s.last_name?.toLowerCase().includes(q) ||
          s.email?.toLowerCase().includes(q)
        );
      })
      .map((s) => ({
        id: s.id,
        label: getDisplayName(s),
        description: s.email,
      }));
  }, [students, studentSearch]);

  // Fetch student analytics
  const { data: analyticsRes, isLoading: analyticsLoading } = useQuery({
    queryKey: ['student-progress-analytics', selectedStudentId],
    queryFn: () => analyticsApi.getStudentAnalytics(selectedStudentId),
    enabled: !!selectedStudentId,
    staleTime: 30000,
  });

  // Fetch student plagiarism summary
  const { data: plagiarismRes } = useQuery({
    queryKey: ['student-plagiarism-summary', selectedStudentId],
    queryFn: () => plagiarismApi.getStudentSummary(selectedStudentId),
    enabled: !!selectedStudentId,
    staleTime: 30000,
  });

  const plagiarismSummary = plagiarismRes?.data?.data as {
    total_exams_flagged: number;
    total_results: number;
    confirmed_count: number;
    dismissed_count: number;
    pending_count: number;
    severity_breakdown: Record<string, number>;
    results: Array<{
      id: string;
      exam_id: string;
      overall_score: number;
      severity: string;
      status: string;
      created_at: string;
    }>;
  } | null;

  const analytics: StudentAnalytics | null = (analyticsRes?.data?.data || null) as StudentAnalytics | null;

  // Prepare chart data from exam history
  const chartData = useMemo(() => {
    if (!analytics?.exams || analytics.exams.length === 0) return [];
    return analytics.exams
      .map((exam) => ({
        date: exam.attempt_date
          ? format(new Date(exam.attempt_date), 'MMM dd')
          : 'N/A',
        score: exam.percentage ?? exam.score ?? 0,
        exam: exam.exam_title,
        rawDate: exam.attempt_date || '',
      }))
      .sort((a, b) => new Date(a.rawDate).getTime() - new Date(b.rawDate).getTime());
  }, [analytics]);

  const handleStudentSelect = (suggestion: { id: string | number; label: string }) => {
    setSelectedStudentId(String(suggestion.id));
    setStudentSearch(suggestion.label);
  };

  // Fetch tenant analytics for top-level stats
  const { data: tenantRes } = useQuery({
    queryKey: ['tenant-analytics-progress'],
    queryFn: () => analyticsApi.getTenantAnalytics(),
    staleTime: 60000,
  });

  const tenantStats = tenantRes?.data?.data as {
    total_exams?: number;
    total_attempts?: number;
    total_students?: number;
    active_exams?: number;
    average_pass_rate?: number;
  } | null;

  const passRate = analytics
    ? analytics.pass_rate ?? (analytics.total_exams_taken > 0
      ? (analytics.total_exams_passed / analytics.total_exams_taken) * 100
      : 0)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Student Progress</h1>
          <p className="text-sm text-white/50 mt-1">Track individual student performance and progress</p>
        </div>
      </div>

      {/* Tenant Stats */}
      {tenantStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard
            title="Total Exams"
            value={tenantStats.total_exams ?? 0}
            icon={<BookOpen className="w-4 h-4" />}
            color="teal"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Attempts"
            value={tenantStats.total_attempts ?? 0}
            icon={<TrendingUp className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Students"
            value={tenantStats.total_students ?? 0}
            icon={<Users className="w-4 h-4" />}
            color="purple"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Active Exams"
            value={tenantStats.active_exams ?? 0}
            icon={<Activity className="w-4 h-4" />}
            color="green"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Avg Pass Rate"
            value={`${(tenantStats.average_pass_rate ?? 0).toFixed(1)}%`}
            icon={<Target className="w-4 h-4" />}
            color="orange"
            className="!p-3 [&_h3]:!text-xl"
          />
        </div>
      )}

      {/* Student Selector */}
      <div className="rounded-xl border border-white/[0.06] p-4" style={{ background: '#0d0d12' }}>
        <label className="block text-xs font-medium text-white/50 mb-1.5">Select Student</label>
        <div className="max-w-md">
          <SearchInput
            placeholder="Search students..."
            value={studentSearch}
            onChange={(value) => setStudentSearch(value)}
            suggestions={studentSuggestions}
            onSuggestionSelect={handleStudentSelect}
            loading={studentsLoading}
            showClear
            size="md"
          />
        </div>
      </div>

      {selectedStudentId && (
        <>
          {analyticsLoading ? (
            <div className="text-center text-white/50 py-8">Loading student analytics...</div>
          ) : !analytics ? (
            <Card>
              <div className="p-8 text-center">
                <User className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">No analytics data available for this student</p>
              </div>
            </Card>
          ) : (
            <>
              {/* Stat Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                  title="Total Exams"
                  value={analytics.total_exams_taken || 0}
                  icon={<BookOpen className="w-4 h-4" />}
                  color="teal"
                  className="!p-3 [&_h3]:!text-xl"
                />
                <StatCard
                  title="Avg Score"
                  value={analytics.average_score != null ? `${analytics.average_score.toFixed(1)}%` : '--'}
                  icon={<TrendingUp className="w-4 h-4" />}
                  color="blue"
                  className="!p-3 [&_h3]:!text-xl"
                />
                <StatCard
                  title="Pass Rate"
                  value={passRate != null ? `${passRate.toFixed(1)}%` : '--'}
                  icon={<Target className="w-4 h-4" />}
                  color="green"
                  className="!p-3 [&_h3]:!text-xl"
                />
                <StatCard
                  title="Highest Score"
                  value={analytics.highest_score != null ? `${analytics.highest_score.toFixed(1)}%` : '--'}
                  icon={<Award className="w-4 h-4" />}
                  color="purple"
                  className="!p-3 [&_h3]:!text-xl"
                />
              </div>

              {/* Score Trend Chart */}
              {chartData.length > 0 && (
                <Card>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-white/90 mb-6">Score Trend</h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis
                            dataKey="date"
                            tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                            axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                            tickLine={false}
                          />
                          <YAxis
                            domain={[0, 100]}
                            tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                            axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1a1f2e',
                              border: '1px solid rgba(255,255,255,0.06)',
                              borderRadius: '8px',
                              color: 'rgba(255,255,255,0.9)',
                            }}
                            labelStyle={{ color: 'rgba(255,255,255,0.5)' }}
                            formatter={(value: number, _name: string, props: any) => [
                              `${value.toFixed(1)}%`,
                              props.payload.exam || 'Score',
                            ]}
                          />
                          <Line
                            type="monotone"
                            dataKey="score"
                            stroke="#14b8a6"
                            strokeWidth={2}
                            dot={{ fill: '#14b8a6', r: 4 }}
                            activeDot={{ r: 6, stroke: '#14b8a6', strokeWidth: 2, fill: '#0d9488' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Card>
              )}

              {/* Exam History Table */}
              <Card>
                <div className="p-6 pb-0">
                  <h3 className="text-lg font-semibold text-white/90 mb-4">Exam History</h3>
                </div>
                {analytics.exams && analytics.exams.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/[0.06]">
                          <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Exam</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Score</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Percentage</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.exams.map((exam, idx) => (
                          <tr
                            key={`${exam.exam_id}-${idx}`}
                            className="border-b border-white/[0.06] hover:bg-white/[0.02]"
                          >
                            <td className="py-3 px-4">
                              <span className="text-sm font-medium text-white/90">{exam.exam_title}</span>
                            </td>
                            <td className="py-3 px-4 text-sm text-white/60">
                              {exam.attempt_date
                                ? format(new Date(exam.attempt_date), 'MMM dd, yyyy')
                                : '--'}
                            </td>
                            <td className="py-3 px-4 text-sm text-white/80">
                              {exam.score != null ? exam.score.toFixed(1) : '--'}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`text-sm font-medium ${
                                  (exam.percentage ?? 0) >= 80
                                    ? 'text-emerald-400'
                                    : (exam.percentage ?? 0) >= 60
                                      ? 'text-blue-400'
                                      : (exam.percentage ?? 0) >= 40
                                        ? 'text-amber-400'
                                        : 'text-red-400'
                                }`}
                              >
                                {exam.percentage != null ? `${exam.percentage.toFixed(1)}%` : '--'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <Badge
                                variant={exam.passed ? 'success' : 'danger'}
                                size="sm"
                              >
                                {exam.passed ? 'Passed' : 'Failed'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/60">No exam history available</p>
                  </div>
                )}
              </Card>

              {/* Additional Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <div className="p-5">
                    <h4 className="text-sm font-semibold text-white/90 mb-3">Performance Summary</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/50">Total Exams Taken</span>
                        <span className="text-sm font-medium text-white/90">{analytics.total_exams_taken || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/50">Exams Passed</span>
                        <span className="text-sm font-medium text-emerald-400">{analytics.total_exams_passed || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/50">Exams Failed</span>
                        <span className="text-sm font-medium text-red-400">
                          {(analytics.total_exams_taken || 0) - (analytics.total_exams_passed || 0)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/50">Total Violations</span>
                        <span className="text-sm font-medium text-amber-400">
                          <span className="inline-flex items-center gap-1">
                            {(analytics.total_violations || 0) > 0 && <AlertTriangle className="w-3 h-3" />}
                            {analytics.total_violations || 0}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="p-5">
                    <h4 className="text-sm font-semibold text-white/90 mb-3">Score Range</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/50">Highest Score</span>
                        <span className="text-sm font-medium text-emerald-400">
                          {analytics.highest_score != null ? `${analytics.highest_score.toFixed(1)}%` : '--'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/50">Lowest Score</span>
                        <span className="text-sm font-medium text-red-400">
                          {analytics.lowest_score != null ? `${analytics.lowest_score.toFixed(1)}%` : '--'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/50">Average Score</span>
                        <span className="text-sm font-medium text-teal-400">
                          {analytics.average_score != null ? `${analytics.average_score.toFixed(1)}%` : '--'}
                        </span>
                      </div>
                      {analytics.rank != null && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/50">Rank</span>
                          <Badge variant="info" size="sm">#{analytics.rank}</Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              {/* Plagiarism History */}
              {plagiarismSummary && plagiarismSummary.total_results > 0 && (
                <Card>
                  <div className="p-6 pb-0">
                    <h3 className="text-lg font-semibold text-white/90 mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-rose-400" />
                      Plagiarism History
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-6 pb-4">
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3">
                      <p className="text-xs text-white/50">Exams Flagged</p>
                      <p className="text-lg font-semibold text-white/90">{plagiarismSummary.total_exams_flagged}</p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3">
                      <p className="text-xs text-white/50">Confirmed</p>
                      <p className="text-lg font-semibold text-rose-400">{plagiarismSummary.confirmed_count}</p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3">
                      <p className="text-xs text-white/50">Dismissed</p>
                      <p className="text-lg font-semibold text-emerald-400">{plagiarismSummary.dismissed_count}</p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3">
                      <p className="text-xs text-white/50">Pending</p>
                      <p className="text-lg font-semibold text-amber-400">{plagiarismSummary.pending_count}</p>
                    </div>
                  </div>
                  {plagiarismSummary.results.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/[0.06]">
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Exam</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Similarity</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Severity</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-white/50">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plagiarismSummary.results.map((r) => (
                            <tr key={r.id} className="border-b border-white/[0.06] hover:bg-white/[0.02]">
                              <td className="py-3 px-4 text-sm text-white/70 font-mono">{r.exam_id.slice(0, 8)}...</td>
                              <td className="py-3 px-4">
                                <span className={`text-sm font-medium ${
                                  r.overall_score >= 0.8 ? 'text-rose-400' :
                                  r.overall_score >= 0.6 ? 'text-amber-400' :
                                  r.overall_score >= 0.4 ? 'text-sky-400' : 'text-emerald-400'
                                }`}>
                                  {(r.overall_score * 100).toFixed(1)}%
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant={
                                  r.severity === 'critical' ? 'danger' :
                                  r.severity === 'high' ? 'warning' :
                                  r.severity === 'medium' ? 'info' : 'secondary'
                                } size="sm">
                                  {r.severity}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant={
                                  r.status === 'confirmed' ? 'danger' :
                                  r.status === 'dismissed' ? 'success' : 'secondary'
                                } size="sm">
                                  {r.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-sm text-white/50">
                                {r.created_at ? new Date(r.created_at).toLocaleDateString() : '--'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
